// ─── Realm buckets ──────────────────────────────────────────────────
// Curated arenas: themed groups of realms a player picks instead of (or
// alongside) a single subject. Four buckets, mutually exclusive, every
// realm in exactly one home. Design: docs/wot_game_modes_design.md.
//
// Why they exist: a single realm is a subject, a bucket is a mood, and
// fewer arenas concentrate the multiplayer pool. The daily rotation below
// is what lets a small player base actually find matches: everyone who
// presses quick match on the same day lands in the same arena.

import { REALMS, type RealmId } from './realms';

export const BUCKET_IDS = ['PRIME_TIME', 'FRONTIER', 'GRAND_TOUR', 'AFTER_DARK'] as const;
export type BucketId = (typeof BUCKET_IDS)[number];

export interface Bucket {
  id: BucketId;
  /** Card title. UI language is English. */
  name: string;
  /** The mood, in one line. */
  tagline: string;
  realms: readonly RealmId[];
  /**
   * Card accent. Borrowed from the member realm whose colour carries the
   * bucket's mood best; buckets deliberately do not get new hues, the
   * palette stays the ten realm colours.
   */
  color: string;
}

export const BUCKETS: Record<BucketId, Bucket> = {
  // Saturday night: music, film, the match. The broadest crowd-pleaser.
  PRIME_TIME: {
    id: 'PRIME_TIME',
    name: 'Prime Time',
    tagline: 'Music, movies, and the big game.',
    realms: ['SOUNDWAVE', 'SPOTLIGHT', 'ARENA'],
    color: REALMS.SPOTLIGHT.color,
  },

  // The edge of knowledge: science, space, technology. Nerd pride.
  FRONTIER: {
    id: 'FRONTIER',
    name: 'Frontier',
    tagline: 'Science, space, and machines.',
    realms: ['EUREKA', 'COSMOS', 'NEXUS'],
    color: REALMS.COSMOS.color,
  },

  // A trip around the world: the places and their tables.
  GRAND_TOUR: {
    id: 'GRAND_TOUR',
    name: 'Grand Tour',
    tagline: 'The world and what it eats.',
    realms: ['ATLAS', 'FEAST'],
    color: REALMS.ATLAS.color,
  },

  // Campfire stories: myths, mysteries, and empires long gone.
  AFTER_DARK: {
    id: 'AFTER_DARK',
    name: 'After Dark',
    tagline: 'Myths, mysteries, and lost empires.',
    realms: ['ENIGMA', 'CHRONICLES'],
    color: REALMS.ENIGMA.color,
  },
} as const;

/**
 * A bucket plays as hard as its members do, on average. The mean rather
 * than the max, because one tough member realm should tilt the mark, not
 * define it (design doc lists this as the chosen answer to an open
 * question). Rounds half-up so a 1.5 reads as 2, not 1.
 */
export function bucketChallenge(id: BucketId): 1 | 2 | 3 {
  const members = BUCKETS[id].realms;
  const mean = members.reduce((n, r) => n + REALMS[r].challenge, 0) / members.length;
  return Math.min(3, Math.max(1, Math.round(mean))) as 1 | 2 | 3;
}

/** Every realm must belong to exactly one bucket; the test suite calls this. */
export function bucketOf(realm: RealmId): BucketId {
  const hit = BUCKET_IDS.find(id => BUCKETS[id].realms.includes(realm));
  if (!hit) throw new Error(`Realm ${realm} belongs to no bucket`);
  return hit;
}

// ── Daily rotation ──────────────────────────────────────────────────
// Quick match plays the arena of the day. Deterministic from the date, so
// every client and the server agree with zero coordination; day boundary
// is UTC on purpose, one global switchover instant rather than a rolling
// one per timezone. The server holds the same formula inside the RPC, and
// app_settings can pin an override for event weeks.

export type ArenaOfDay = 'ALL' | BucketId;

const ROTATION: readonly ArenaOfDay[] = ['ALL', ...BUCKET_IDS];

export function arenaOfDay(date: Date = new Date()): ArenaOfDay {
  const epochDay = Math.floor(date.getTime() / 86_400_000);
  return ROTATION[epochDay % ROTATION.length];
}

/** The realms a given arena actually serves; 'ALL' means no filter. */
export function arenaRealms(arena: ArenaOfDay): readonly RealmId[] | null {
  return arena === 'ALL' ? null : BUCKETS[arena].realms;
}
